import { useEffect, useRef, useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface StatItem {
  value: number;
  suffix: string;
  prefix?: string;
  label: string;
  sublabel: string;
  icon: string;
}

const stats: StatItem[] = [
  {
    value: 1,
    suffix: '',
    label: 'Year of Excellence',
    sublabel: 'Mobile-based and growing since day one',
    icon: 'ri-award-line',
  },
  {
    value: 30,
    suffix: '+',
    label: 'Students Developed',
    sublabel: 'Real people. Real growth. Real results.',
    icon: 'ri-user-heart-line',
  },
  {
    value: 5,
    suffix: '+',
    label: 'Programs & Services',
    sublabel: 'From on-site to in-home to studio sessions',
    icon: 'ri-layout-grid-line',
  },
  {
    value: 100,
    suffix: '%',
    label: 'Inclusive Environment',
    sublabel: 'Every background, every ability, welcome',
    icon: 'ri-heart-line',
  },
];

function useCounter(target: number, isVisible: boolean, duration = 1600) {
  const [count, setCount] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    if (!isVisible || started.current) return;
    started.current = true;
    const steps = 40;
    const stepTime = duration / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += 1;
      setCount(Math.round((target / steps) * current));
      if (current >= steps) {
        setCount(target);
        clearInterval(timer);
      }
    }, stepTime);
    return () => clearInterval(timer);
  }, [isVisible, target, duration]);

  return count;
}

function StatCard({ stat, isVisible, delay }: { stat: StatItem; isVisible: boolean; delay: number }) {
  const count = useCounter(stat.value, isVisible);

  return (
    <div
      className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} flex flex-col items-center text-center px-6 py-10 relative group`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Icon */}
      <div
        className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-brand-yellow/15 text-brand-yellow-dark mb-5 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark transition-all duration-300 wiggle-hover`}
        style={{ transitionDelay: `${delay + 120}ms` }}
      >
        <i className={`${stat.icon} text-2xl`} />
      </div>

      {/* Number */}
      <div className="flex items-end gap-0.5 mb-2">
        {stat.prefix && (
          <span className="font-heading text-3xl font-black text-brand-blue-dark leading-none mb-1">{stat.prefix}</span>
        )}
        <span className="font-heading text-5xl xl:text-6xl font-black text-brand-blue-dark leading-none tabular-nums">
          {count}
        </span>
        <span className="font-heading text-3xl font-black text-brand-yellow leading-none mb-1">{stat.suffix}</span>
      </div>

      {/* Label */}
      <p className="font-heading text-base font-bold text-brand-blue-dark mb-1">{stat.label}</p>
      <p className="font-body text-gray-400 text-xs leading-relaxed max-w-[160px]">{stat.sublabel}</p>

      {/* Divider — hidden on last */}
      <div className="absolute right-0 top-1/4 h-1/2 w-px bg-gray-100 hidden lg:block last:hidden" />
    </div>
  );
}

export default function StatsSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section ref={ref} className="bg-white border-y border-gray-100 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8">
        {/* Top label */}
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center pt-12 pb-2`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-blue/8 text-brand-blue-dark font-body text-xs uppercase tracking-widest font-semibold border border-brand-blue/10">
            By the Numbers
          </span>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x-0 lg:divide-x divide-gray-100">
          {stats.map((stat, i) => (
            <StatCard key={stat.label} stat={stat} isVisible={isVisible} delay={i * 120} />
          ))}
        </div>

        {/* Bottom strip */}
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} border-t border-gray-100 py-5 flex flex-col sm:flex-row items-center justify-center gap-2 text-center`} style={{ transitionDelay: '500ms' }}>
          <i className="ri-car-line text-brand-yellow text-base" />
          <p className="font-body text-gray-400 text-xs">
            Mobile-based — proudly serving <strong className="text-brand-blue-dark font-semibold">multiple cities across North Carolina</strong> · We come to you
          </p>
        </div>
      </div>
    </section>
  );
}
